import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Power, MapPin, Package, Play, Square, ChevronRight, RefreshCw, Truck, Flame } from "lucide-react";
import MapView from "@/components/MapView";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { useSeo } from "@/hooks/useSeo";

const SERVICES = [
  { key: "food",     label: "Food"     },
  { key: "bike",     label: "Bike"     },
  { key: "mart",     label: "Mart"     },
  { key: "ride",     label: "Ride"     },
  { key: "car",      label: "Car"      },
  { key: "express",  label: "Express"  },
  { key: "dine_out", label: "Dine Out" },
  { key: "gifts",    label: "Gifts"    },
];

const PING_MS = 20_000;

/**
 * /provider/shift — Slice I · Grab-style provider dashboard.
 *
 * · Big Online / Offline toggle
 * · Service picker (one at a time)
 * · Auto-GPS ping every 20 s (best-effort)
 * · Live queue of nearby unassigned deliveries
 * · Accept → jumps into /ride/:id from the driver seat
 */
export default function ProviderShift() {
  const { user, loading: authLoading } = useAuth();
  const { loc } = useUserLocation();
  const navigate = useNavigate();
  const [shift, setShift]       = useState(null);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [service, setService]   = useState("ride");
  const [plate, setPlate]       = useState("");
  const [err, setErr]           = useState(null);

  const [earnings, setEarnings] = useState(null);
  const [earningsRange, setEarningsRange] = useState("today");

  useSeo({ title: "Driver shift — Find2Near" });

  const loadShift = useCallback(async () => {
    try {
      const r = await api.get("/provider/shift/me");
      setShift(r.data?.online ? r.data : null);
      if (r.data?.service) setService(r.data.service);
    } catch { setShift(null); }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const r = await api.get("/provider/requests", { params: { service, radius_km: 25 } });
      setRequests(r.data?.items || []);
    } catch { setRequests([]); }
  }, [service]);

  useEffect(() => { if (user) loadShift(); }, [user, loadShift]);
  useEffect(() => { if (shift) loadRequests(); }, [shift, service, loadRequests]);
  useEffect(() => {
    if (!user) return;
    api.get("/provider/earnings", { params: { range: earningsRange } })
      .then(r => setEarnings(r.data))
      .catch(() => setEarnings(null));
  }, [user, earningsRange, shift]);

  // Ping loop while online
  useEffect(() => {
    if (!shift) return;
    const t = setInterval(() => {
      if (loc.lat && loc.lng) {
        api.post("/provider/shift/ping", { lat: loc.lat, lng: loc.lng }).catch(() => {});
      }
      loadRequests();
    }, PING_MS);
    return () => clearInterval(t);
  }, [shift, loc.lat, loc.lng, loadRequests]);

  const goOnline = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/provider/shift", {
        service,
        lat: loc.lat || 19.076,
        lng: loc.lng || 72.8777,
        vehicle_plate: plate || undefined,
      });
      setShift(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start shift");
    } finally { setBusy(false); }
  };

  const goOffline = async () => {
    setBusy(true);
    try { await api.post("/provider/shift/end"); setShift(null); setRequests([]); }
    finally { setBusy(false); }
  };

  const accept = async (id) => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/delivery/${id}/accept`);
      navigate(`/ride/${id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Accept failed — someone may have already taken it.");
      loadRequests();
    } finally { setBusy(false); }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 px-6 text-center" data-testid="provider-signin">
        <Truck size={32} className="mx-auto text-stone-300 mb-3" />
        <div className="heading font-bold text-xl">Driver shift</div>
        <div className="text-sm text-stone-500 mt-2">Sign in to go online and accept nearby requests.</div>
        <Link to="/account" className="mt-4 inline-flex rounded-full bg-[#E05E36] text-white font-bold px-5 py-2 text-sm">Sign in →</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="provider-shift">
      {/* Status pill */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Driver shift</div>
          <h1 className="heading font-black text-2xl md:text-3xl text-stone-900">
            {shift ? "You are online" : "You are offline"}
          </h1>
          <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
            <MapPin size={11} /> {loc.city || "Detecting location…"}
          </div>
        </div>
        {shift ? (
          <button onClick={goOffline} disabled={busy} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-1 disabled:opacity-50" data-testid="provider-go-offline">
            <Square size={12} /> Go offline
          </button>
        ) : (
          <button onClick={goOnline} disabled={busy || !loc.lat} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-1 disabled:opacity-50" data-testid="provider-go-online">
            <Play size={12} /> Go online
          </button>
        )}
      </div>

      {err && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="provider-error">
          {err}
        </div>
      )}

      {/* Earnings card */}
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white rounded-2xl p-4 mb-4" data-testid="provider-earnings">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-100">Earnings</div>
          <div className="flex gap-1">
            {["today", "week"].map(r => (
              <button
                key={r}
                onClick={() => setEarningsRange(r)}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${earningsRange === r ? "bg-white text-emerald-800" : "bg-emerald-500/40 text-white"}`}
                data-testid={`earnings-range-${r}`}
              >
                {r === "today" ? "Today" : "This week"}
              </button>
            ))}
          </div>
        </div>
        <div className="heading font-black text-3xl">
          ₹{(earnings?.earnings_inr ?? 0).toLocaleString("en-IN")}
        </div>
        <div className="text-xs text-emerald-100 mt-1">
          {earnings?.trips ?? 0} trips · {earnings?.distance_km ?? 0} km
        </div>
        <WithdrawButton onDone={() => { setEarningsRange(earningsRange); }} disabled={(earnings?.earnings_inr ?? 0) <= 0} />
      </div>

      {/* Payout history */}
      <PayoutLedger />

      {/* Demand heat-map */}
      <DemandHeatmap service={service} loc={loc} enabled={!!shift} />

      {/* Service + vehicle */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Service</div>
        <div className="flex gap-1.5 flex-wrap" data-testid="provider-services">
          {SERVICES.map(s => (
            <button
              key={s.key}
              onClick={() => setService(s.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${service === s.key ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200"}`}
              data-testid={`provider-service-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {!shift && (
          <div className="mt-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Vehicle plate (optional)</div>
              <input value={plate} onChange={e => setPlate(e.target.value.toUpperCase())} placeholder="MH02 AB 1234" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm" data-testid="provider-plate" />
            </label>
          </div>
        )}
      </div>

      {/* Live queue */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid="provider-queue">
        <div className="flex items-center justify-between mb-3">
          <div className="heading font-bold text-lg text-stone-900 flex items-center gap-2">
            <Package size={16} className="text-emerald-700" /> Nearby requests
          </div>
          <button onClick={loadRequests} className="p-1.5 rounded-full hover:bg-stone-100" title="Refresh" data-testid="provider-refresh">
            <RefreshCw size={14} className="text-stone-500" />
          </button>
        </div>
        {!shift ? (
          <div className="text-sm text-stone-500 text-center py-6">
            Go online to see requests within your service radius.
          </div>
        ) : requests.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-6" data-testid="provider-queue-empty">
            No requests yet. New ones will appear here as citizens book.
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map(r => (
              <button
                key={r.delivery_id}
                onClick={() => accept(r.delivery_id)}
                disabled={busy}
                className="w-full text-left flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl p-3 hover:border-emerald-400 disabled:opacity-50"
                data-testid={`provider-req-${r.delivery_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-white grid place-items-center border border-stone-200">
                  <Package size={14} className="text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-stone-900 truncate">
                    {r.pickup?.address || "Pickup"}
                  </div>
                  <div className="text-[11px] text-stone-500 truncate">
                    → {r.dropoff?.address || "Drop-off"}
                    {r.distance_km !== undefined && r.distance_km !== null && (
                      <span className="ml-2 text-emerald-700 font-bold">{r.distance_km.toFixed(1)} km away</span>
                    )}
                  </div>
                </div>
                <ChevronRight size={18} className="text-stone-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WithdrawButton({ onDone, disabled }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const withdraw = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/provider/earnings/withdraw");
      setMsg(`₹${r.data.amount_inr} added to your wallet · ${r.data.trips} trips`);
      onDone?.();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Withdraw failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button
        onClick={withdraw}
        disabled={busy || disabled}
        className="w-full rounded-full bg-white text-emerald-800 font-bold py-2 text-sm disabled:opacity-50"
        data-testid="provider-withdraw"
      >
        {busy ? "Moving to wallet…" : disabled ? "No earnings yet" : "Withdraw to Find2Near wallet"}
      </button>
      {msg && (
        <div className="mt-2 text-[11px] text-white/90" data-testid="provider-withdraw-msg">{msg}</div>
      )}
    </div>
  );
}



function PayoutLedger() {
  const [items, setItems]     = useState([]);
  const [lifetime, setLife]   = useState(0);
  const [open, setOpen]       = useState(false);
  const [loaded, setLoaded]   = useState(false);
  useEffect(() => {
    api.get("/provider/payouts").then(r => {
      setItems(r.data?.items || []);
      setLife(r.data?.lifetime_inr || 0);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  if (!loaded) return null;
  return (
    <div className="mb-4 bg-white border border-stone-200 rounded-2xl" data-testid="payout-ledger">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4"
        data-testid="payout-ledger-toggle"
      >
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Payout history</div>
          <div className="text-sm text-stone-700">
            <b>₹{lifetime.toLocaleString("en-IN")}</b> lifetime · {items.length} withdrawal{items.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="text-stone-400">{open ? "▲" : "▼"}</div>
      </button>
      {open && (
        <div className="border-t border-stone-100 max-h-64 overflow-y-auto" data-testid="payout-ledger-list">
          {items.length === 0 ? (
            <div className="p-4 text-sm text-stone-500 text-center">
              No withdrawals yet — earnings will show up here after your first payout.
            </div>
          ) : items.map(p => (
            <div key={p.payout_id} className="px-4 py-3 border-b border-stone-100 last:border-0 flex items-center justify-between" data-testid={`payout-row-${p.payout_id}`}>
              <div>
                <div className="font-bold text-stone-900">₹{Number(p.amount_inr || 0).toLocaleString("en-IN")}</div>
                <div className="text-[11px] text-stone-500">
                  {p.trips} trip{p.trips === 1 ? "" : "s"} · week of {p.week_start?.slice(0, 10)}
                </div>
              </div>
              <div className="text-[10px] text-stone-400 font-mono">{p.wallet_txn_id?.slice(0, 12) || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function DemandHeatmap({ service, loc, enabled }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    const fetchIt = () => api.get("/provider/heatmap", { params: { service, lat: loc.lat, lng: loc.lng, radius_km: 25 } })
      .then(r => setData(r.data))
      .catch(() => setData(null));
    fetchIt();
    const t = setInterval(fetchIt, 30_000);
    return () => clearInterval(t);
  }, [enabled, service, loc.lat, loc.lng]);
  if (!enabled) return null;
  const markers = [];
  (data?.demand || []).forEach(c => markers.push({
    key: `d_${c.lat}_${c.lng}`, lat: c.lat, lng: c.lng,
    label: `${c.count}`, pulse: c.count >= 3,
    popup: <b>Demand: {c.count} requests</b>,
  }));
  (data?.providers || []).forEach((p, i) => markers.push({
    key: `p_${i}`, lat: p.lat, lng: p.lng, label: "🚕",
    popup: <span>Driver</span>,
  }));
  const center = loc.lat && loc.lng ? [loc.lat, loc.lng] : [19.076, 72.8777];
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="provider-heatmap">
      <div className="p-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 inline-flex items-center gap-1">
          <Flame size={12} className="text-rose-500" /> Where demand is right now
        </div>
        <div className="text-[10px] text-stone-500">
          {data?.demand?.length || 0} zones · {data?.providers?.length || 0} drivers
        </div>
      </div>
      <div className="relative h-56">
        <MapView center={center} zoom={12} markers={markers} height="100%" />
      </div>
      {data?.hot_zones?.length > 0 && (
        <div className="p-3 border-t border-stone-100 flex gap-2 overflow-x-auto no-scrollbar">
          {data.hot_zones.map((c, i) => (
            <div key={i} className="text-[11px] font-bold rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-2.5 py-1 whitespace-nowrap" data-testid={`heat-zone-${i}`}>
              🔥 {c.count} req · {c.lat.toFixed(2)},{c.lng.toFixed(2)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

